import { defineStore } from 'pinia'
import { menuFavorite, menuUnfavorite, getReportConfigRemark } from '@/api'
import { useUserStore } from '@/store/modules/user'
import { useI18n } from '@/hooks/web/useI18n'
import { useBreadcrumb } from '@/hooks/web/useBreadcrumb'
import { store } from '../index'

interface AppState {
  // 菜单相关变量
  isOnlyPage: boolean
  collapse: boolean
  sidebarWidth: number
  // 收藏&历史记录
  historyList: Array<quickstartRoute>
  collectList: Array<quickstartRoute>
  // 菜单查询历史
  searchHistory: object

  pageLoading: boolean
  // title: string
  tooltipVisible: boolean
  tooltipVirtualRef: HTMLElement | null
  nounDictionary: object | null

  // 退出登录前钩子
  beforeLogoutHook: Array<() => Promise<unknown>>
}

export const useAppStore = defineStore('app', {
  state: (): AppState => {
    return {
      isOnlyPage: true,
      collapse: false, // 折叠菜单
      sidebarWidth: 272, // 菜单栏宽度
      historyList: [],
      collectList: [],
      searchHistory: {},

      pageLoading: false, // 路由跳转loading
      // title: import.meta.env.VITE_APP_TITLE, // 标题
      // 全局单例tooltip
      tooltipVisible: false,
      tooltipVirtualRef: null,
      nounDictionary: null,
      beforeLogoutHook: []
    }
  },
  getters: {
    getIsOnlyPage(): boolean {
      return this.isOnlyPage
    },
    getCollapse(): boolean {
      return this.collapse
    },
    getSidebarWidth(): number {
      return this.sidebarWidth
    },
    getHistoryList(): Array<quickstartRoute> {
      return this.historyList
    },
    getCollectList(): Array<quickstartRoute> {
      return this.collectList
    },
    getSearchHistory(): Array<string> {
      const userStore = useUserStore()
      const accountCode = userStore.getUserInfo?.accountCode
      return this.searchHistory[accountCode] || []
    },
    getPageLoading(): boolean {
      return this.pageLoading
    },
    getNounDictionary(): object {
      return this.nounDictionary || {}
    }
  },
  actions: {
    setIsOnlyPage(isOnlyPage: boolean) {
      this.isOnlyPage = isOnlyPage
    },
    setCollapse(collapse: boolean) {
      this.collapse = collapse
    },
    setSidebarWidth(width: number) {
      this.sidebarWidth = width
    },
    setHistoryList(list: Array<quickstartRoute>) {
      this.historyList = list
    },
    addHistoryList(item: quickstartRoute) {
      const index = this.historyList.findIndex((h) => h.fullPath === item.fullPath)
      if (index > -1) {
        this.historyList.splice(index, 1)
      }
      this.historyList.unshift(item)
      if (this.historyList.length > 5) {
        this.historyList.pop()
      }
    },
    toggleCollect(item: quickstartRoute) {
      const { permissionCode, modelCode, pageId, title } = item.meta ?? {}
      const index = this.collectList.findIndex((h) => {
        return h.meta.permissionCode === permissionCode && h.meta.modelCode == modelCode
      })
      if (index > -1) {
        this.collectList.splice(index, 1)
        menuUnfavorite({
          permissionCode,
          modelCode
        })
      } else {
        const { getBreadcrumb } = useBreadcrumb()
        const { t } = useI18n()
        const levelList = getBreadcrumb(item)
        const menuRouteDesc = levelList.map((r) =>
          //临时处理主数据国际化
          // t(r.meta?.pageId || r.meta?.modelName || r.meta?.title)
          t(r.meta?.pageId || 'masterData.search.member' || r.meta?.title)
        )
        this.collectList.push(item)
        menuFavorite({
          permissionCode,
          modelCode,
          menuName: title,
          pageId,
          menuRouteDesc: menuRouteDesc.join(' / ')
        })
      }
    },
    setCollectList(list: Array<quickstartRoute>) {
      this.collectList = list
    },
    addSearchHistory(keyword: string) {
      const userStore = useUserStore()
      const accountCode = userStore.getUserInfo?.accountCode
      if (!this.searchHistory[accountCode]) {
        this.searchHistory[accountCode] = []
      }
      const currentSearchHistory = this.searchHistory[accountCode]
      const index = currentSearchHistory.findIndex((h) => h === keyword)
      if (index > -1) {
        currentSearchHistory.splice(index, 1)
      }
      currentSearchHistory.unshift(keyword)
    },
    deleteSearchHistory(keyword: string) {
      const userStore = useUserStore()
      const accountCode = userStore.getUserInfo?.accountCode
      const currentSearchHistory = this.searchHistory[accountCode]
      const index = currentSearchHistory.findIndex((h) => h === keyword)
      if (index > -1) {
        currentSearchHistory.splice(index, 1)
      }
    },
    setPageLoading(pageLoading: boolean) {
      this.pageLoading = pageLoading
    },
    // setTitle(title: string) {
    //   this.title = title
    // },
    setTooltipVisible(visible: boolean) {
      this.tooltipVisible = visible
    },
    setTooltipVirtualRef(virtualRef: HTMLElement | null) {
      this.tooltipVirtualRef = virtualRef
    },
    // 获取指标释义
    fetchNounDictionary() {
      getReportConfigRemark('all').then((res) => {
        this.nounDictionary = res.data
      })
    },
    updateNounDictionary(key) {
      getReportConfigRemark(key).then((res) => {
        this.nounDictionary[key] = res.data[key]
      })
    },
    setBeforeLogoutHook(hook: () => Promise<unknown>) {
      const index = this.beforeLogoutHook.findIndex((h) => h === hook)
      if (index === -1) {
        this.beforeLogoutHook.push(hook)
      }
      this.beforeLogoutHook = this.beforeLogoutHook.filter((h) => !!h)
    },
    removeBeforeLogoutHook(hook: () => Promise<unknown>) {
      const index = this.beforeLogoutHook.findIndex((h) => h === hook)
      if (index > -1) {
        this.beforeLogoutHook.splice(index, 1)
      }
    },
    waitBeforeLogout() {
      return Promise.allSettled(
        this.beforeLogoutHook?.filter((hook) => !!hook).map((hook) => hook())
      )
    }
  },
  persist: true
})

export const useAppStoreWithOut = () => {
  return useAppStore(store)
}
